import { useQuery } from 'react-query'
import { 
  Users, 
  Bot, 
  Phone, 
  Settings, 
  Activity, 
  AlertCircle,
  CheckCircle,
  Clock,
  TrendingUp
} from 'lucide-react'
import { adminAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import StatsCard from '../components/StatsCard'

function AdminDashboard() {
  const { user } = useAuth()

  const { data: dashboardData, isLoading } = useQuery(
    'admin-dashboard',
    adminAPI.getDashboard,
    { refetchInterval: 30000 }
  )

  const { data: assignments } = useQuery(
    'admin-assignments',
    adminAPI.getAssignments
  )

  const { data: systemHealth } = useQuery(
    'system-health',
    adminAPI.getSystemHealth,
    { refetchInterval: 60000 }
  )

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-primary-900">Access Denied</h3>
        <p className="mt-1 text-sm text-primary-500">
          You need admin privileges to access this dashboard.
        </p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  const overview = dashboardData?.data?.overview || {}
  const recentActivity = dashboardData?.data?.recentActivity || {}
  const analytics = dashboardData?.data?.analytics || {}
  const unassignedResources = assignments?.data?.unassignedResources || {}

  const systemStats = [
    {
      name: 'Total Users',
      value: overview.totalUsers || 0,
      icon: Users,
      change: '+12%',
      changeType: 'positive'
    },
    {
      name: 'AI Agents',
      value: overview.totalAgents || 0,
      icon: Bot,
      change: '+5%',
      changeType: 'positive'
    },
    {
      name: 'Phone Numbers',
      value: overview.totalPhoneNumbers || 0,
      icon: Phone,
      change: '0%',
      changeType: 'neutral'
    },
    {
      name: 'Applications',
      value: overview.totalApps || 0,
      icon: Settings,
      change: '+2%',
      changeType: 'positive'
    }
  ]

  const getHealthIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      default:
        return <Clock className="h-5 w-5 text-primary-400" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-primary-600">
          System overview and administrative controls
        </p>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {systemStats.map((stat) => (
          <StatsCard key={stat.name} {...stat} />
        ))}
      </div>

      {/* Call Performance */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Total Calls (24h)
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {overview.callsLast24h || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    All Time Calls
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {overview.totalCalls || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-primary-500 truncate">
                    Avg Duration
                  </dt>
                  <dd className="text-lg font-medium text-primary-900">
                    {Math.round(overview.averageDuration || 0)}s
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Health and Unassigned Resources */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* System Health */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-primary-900">System Health</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700">Database</span>
                <div className="flex items-center space-x-2">
                  {getHealthIcon(systemHealth?.data?.health?.database)}
                  <span className="text-sm text-primary-600 capitalize">
                    {systemHealth?.data?.health?.database || 'Unknown'}
                  </span>
                </div>
              </div>
              
              {/* <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700">ElevenLabs API</span>
                <div className="flex items-center space-x-2">
                  {getHealthIcon(systemHealth?.data?.health?.apis?.elevenlabs)}
                  <span className="text-sm text-primary-600 capitalize">
                    {systemHealth?.data?.health?.apis?.elevenlabs || 'Unknown'}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700">Jambonz API</span>
                <div className="flex items-center space-x-2">
                  {getHealthIcon(systemHealth?.data?.health?.apis?.jambonz)}
                  <span className="text-sm text-primary-600 capitalize">
                    {systemHealth?.data?.health?.apis?.jambonz || 'Unknown'}
                  </span>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        {/* Unassigned Resources */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-primary-900">Unassigned Resources</h3>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700">Agents</span>
                <span className="text-sm text-primary-900 font-medium">
                  {unassignedResources.agents?.length || 0}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700">Phone Numbers</span>
                <span className="text-sm text-primary-900 font-medium">
                  {unassignedResources.phoneNumbers?.length || 0}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700">Applications</span>
                <span className="text-sm text-primary-900 font-medium">
                  {unassignedResources.apps?.length || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Calls */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-primary-900">Recent Calls</h3>
          </div>
          <div className="card-body">
            {recentActivity.calls?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.calls.slice(0, 5).map((call) => (
                  <div key={call._id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-primary-900">
                        {call.phoneNumber}
                      </p>
                      <p className="text-xs text-primary-500">
                        {call.from} → {call.to}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`status-badge ${
                        call.status === 'completed' ? 'status-active' : 'status-inactive'
                      }`}>
                        {call.status}
                      </span>
                      <p className="text-xs text-primary-500 mt-1">
                        {call.duration}s
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-primary-500">No recent calls</p>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-primary-900">Recent Users</h3>
          </div>
          <div className="card-body">
            {recentActivity.users?.length > 0 ? (
              <div className="space-y-3">
                {recentActivity.users.map((userData) => (
                  <div key={userData._id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary-600">
                          {userData.firstName?.charAt(0)}{userData.lastName?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-primary-900">
                          {userData.firstName} {userData.lastName}
                        </p>
                        <p className="text-xs text-primary-500">
                          {userData.email}
                        </p>
                      </div>
                    </div>
                    <span className={`status-badge ${
                      userData.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {userData.role}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-primary-500">No recent users</p>
            )}
          </div>
        </div>
      </div>

      {/* Call Status Analytics */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-primary-900">Call Status Distribution</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {analytics.callStatusBreakdown?.map((status) => (
              <div key={status._id} className="text-center">
                <div className="text-2xl font-bold text-primary-900">
                  {status.count}
                </div>
                <div className="text-sm text-primary-600 capitalize">
                  {status._id || 'Unknown'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard